import { Link } from "react-router-dom";
import { EventBadge } from "../../components/EventBadge";
import type { AttentionItem, OperationsSnapshot, TaskStatus, WorkTask } from "../../domain/operations";
import { workClassLabels } from "../../domain/operations";
import { activeAttention, focusTaskForNow, nextTasksForMobile, staffByIdMap, visibleBlocks, visibleTasksInBlock } from "../../domain/operationsQueries";
import { platformServices } from "../../platform/platform";

interface MobileTodayProps {
  snapshot: OperationsSnapshot;
  onStatus(taskId: string, status: TaskStatus): void;
  onBlock(task: WorkTask): void;
  onReview(item: AttentionItem): void;
}

export function MobileToday({ snapshot, onStatus, onBlock, onReview }: MobileTodayProps) {
  const staffById = staffByIdMap(snapshot);
  const nowBlock = visibleBlocks(snapshot)[0];
  const focusTask = focusTaskForNow(snapshot);
  const attention = activeAttention(snapshot);
  const nextTasks = nextTasksForMobile(snapshot, focusTask?.id, 3);
  const laterBlock = visibleBlocks(snapshot).find((block) => block.label === "LATER");
  const event = focusTask ? snapshot.events.find((item) => item.id === focusTask.eventId) : undefined;
  const nowTasks = nowBlock ? visibleTasksInBlock(snapshot, nowBlock) : [];

  return (
    <section className="mobile-view" aria-label="Today's field work">
      <div className="mobile-date"><div><span>FRIDAY · SEP 12</span><h1>Good morning, Alex</h1></div></div>
      {attention[0] && (
        <div className="mobile-alert">
          <span aria-hidden="true">!</span>
          <div><strong>{attention.filter((item) => item.kind === "blocker").length} blocker needs attention</strong><small>{attention[0].title} · {snapshot.events.find((item) => item.id === attention[0].eventId)?.name}</small></div>
          <button onClick={() => onReview(attention[0])}>Review</button>
        </div>
      )}
      {nowBlock && focusTask && event && (
        <>
          <div className="mobile-section-head"><div><span className="live-dot" /><strong>NOW</strong><small>{nowBlock.location}</small></div><span>{nowBlock.timeframe}</span></div>
          <article className="focus-card">
            <div className="focus-top"><EventBadge event={event} /><span className={`tag ${focusTask.classification}`}>{workClassLabels[focusTask.classification]}</span></div>
            <h2>{focusTask.title}</h2>
            <div className="focus-details">
              <button className="detail-link" onClick={() => platformServices.openDirections(nowBlock.location)}>⌖ <b>{nowBlock.location}</b> · {focusTask.detail}</button>
              {focusTask.scheduledTime && <span>◷ Scheduled {focusTask.scheduledTime}</span>}
              {focusTask.status === "eligible" && !focusTask.scheduledTime && <span>Eligible · not tied to Event date</span>}
              {focusTask.assignedTo && <span>♙ Assigned to <b>{staffById[focusTask.assignedTo]?.name}</b></span>}
            </div>
            <div className="task-context">
              <span>{nowTasks.filter((task) => task.status === "complete").length} of {nowTasks.length} in this block</span>
              <div><i style={{ width: `${nowTasks.length ? nowTasks.filter((task) => task.status === "complete").length / nowTasks.length * 100 : 0}%` }} /></div>
            </div>
            {(focusTask.status === "scheduled" || focusTask.status === "eligible") && <button className="start-task" onClick={() => onStatus(focusTask.id, "in_progress")}>Start task <span>→</span></button>}
            {focusTask.status === "in_progress" && <div className="task-actions"><button onClick={() => onStatus(focusTask.id, "complete")}>Complete</button><button onClick={() => onBlock(focusTask)}>Blocked</button></div>}
            {focusTask.status === "blocked" && <div className="task-actions"><button onClick={() => onStatus(focusTask.id, "scheduled")}>Unblock</button><button onClick={() => onStatus(focusTask.id, "complete")}>Complete</button></div>}
            {focusTask.status === "complete" && <button className="start-task complete-action" onClick={() => onStatus(focusTask.id, "scheduled")}>Reopen task</button>}
          </article>
          <Link className="work-block-link" to={`/events/${focusTask.eventId}`}>
            <span className="location-icon">⌖</span>
            <span><strong>Open {event.name}</strong><small>{nowTasks.filter((task) => task.status === "complete").length} complete · {nowTasks.filter((task) => task.status === "blocked").length} blocked · {nowTasks.filter((task) => !["complete", "blocked"].includes(task.status)).length} remaining</small></span>
            <span>›</span>
          </Link>
        </>
      )}
      <div className="mobile-section-head next-head"><div><strong>NEXT</strong><small>Sequenced for your shift</small></div></div>
      <div className="next-list">
        {nextTasks.map((task) => {
          const taskEvent = snapshot.events.find((item) => item.id === task.eventId);
          if (!taskEvent) return null;
          return (
            <article className="next-card" key={task.id}>
              <div className="next-time"><strong>{task.scheduledTime?.split(" ")[0] ?? "—"}</strong><small>{task.scheduledTime?.split(" ")[1] ?? (task.status === "eligible" ? "ELIG" : "TBD")}</small></div>
              <div className="next-content">
                <div><EventBadge compact event={taskEvent} /><span className={`tag ${task.classification}`}>{workClassLabels[task.classification]}</span></div>
                <h3>{task.title}</h3>
                <p>⌖ {task.detail}</p>
                {(task.status === "scheduled" || task.status === "eligible") && <button className="text-button" onClick={() => onStatus(task.id, "in_progress")}>Start</button>}
              </div>
              <Link aria-label={`Open ${taskEvent.name}`} to={`/events/${task.eventId}`}>›</Link>
            </article>
          );
        })}
      </div>
      {laterBlock && (
        <button className="later-card" onClick={() => platformServices.openDirections(laterBlock.location)}>
          <span className="location-icon onsite">⌂</span>
          <span><small>LATER · {laterBlock.time} {laterBlock.meridiem}</small><strong>{laterBlock.title}</strong><span>{visibleTasksInBlock(snapshot, laterBlock).length} tasks · {laterBlock.location}</span></span>
          <span>›</span>
        </button>
      )}
      <div className="mobile-space" />
    </section>
  );
}
